import { UserIcon } from '@heroicons/react/24/outline';
import * as React from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { GroupedCombobox } from '@/components/ui/grouped-combobox';
import { Input } from '@/components/ui/input';
import { INSURANCE_CARRIER_CONTACTS } from '@/constants/insurance-carriers';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import { useGetMatterFlattenedContextQuery } from '@/services/api/matterFlattenedContextService';
import { useGetNotesQuery } from '@/services/api/matterNotesService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';
import { ContactHandleSchema } from '@/services/types/coverage-types';
import { InsuranceNote, NOTE_TYPES } from '@/services/types/matter-notes-types';
import { Contact, ContactType } from '@/services/types/task-types';
import { ContactHandleType } from '@/services/types/treatment-types';
import { isPhoneNumberValid } from '@/utils/validation';

// Format phone number for display
const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';

  // Handle E.164 format
  if (phoneNumber.startsWith('+')) {
    const match = phoneNumber.match(/^\+(\d{1})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
    }
  }

  // Handle standard US formats
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  return phoneNumber;
};

interface CallFormData {
  contact: Contact | null;
  phoneNumber: string;
}

interface CallContactSelectorProps {
  form: UseFormReturn<CallFormData>;
  matterId: string;
  inlineContact?: Contact | null;
}

const useCallContacts = (matterId: string) => {
  const { data: medicalProviders } = useGetMedicalProvidersQuery(matterId);
  const { data: coverages } = useGetCoveragesQuery({ matterId });
  const { data: insuranceNotes } = useGetNotesQuery({
    matterId,
    noteType: NOTE_TYPES.INSURANCE,
  });

  const contacts = React.useMemo(() => {
    const allContacts: Contact[] = [];

    // Medical providers
    if (medicalProviders) {
      allContacts.push(
        ...medicalProviders
          .filter((provider) => provider.phone)
          .map((provider) => ({
            id: provider.id,
            name: provider.name || 'Unnamed Provider',
            phone: provider.phone || '',
            type: ContactType.MEDICAL_PROVIDER,
          })),
      );

      // Provider additional phone handles
      medicalProviders.forEach((provider) => {
        if (provider.handles?.length) {
          const phoneHandles = provider.handles.filter(
            (handle) =>
              handle.handle_type === ContactHandleType.PHONE &&
              handle.value &&
              handle.value !== provider.phone,
          );

          allContacts.push(
            ...phoneHandles.map((handle, index) => ({
              id: `${provider.id}-handle-${index}`,
              name: provider.name || 'Unnamed Provider',
              secondaryTitle: handle.name || 'Phone',
              phone: handle.value || '',
              type: ContactType.MEDICAL_PROVIDER,
            })),
          );
        }
      });
    }

    // Coverage contacts (carriers, adjusters, subrogators)
    if (coverages) {
      coverages.forEach((coverage) => {
        // Carrier contacts
        if (coverage.carrier?.handles) {
          const phoneHandles = coverage.carrier.handles.filter(
            (handle: ContactHandleSchema) =>
              handle.handle_type === 'PHONE' &&
              handle.value &&
              isPhoneNumberValid(handle.value),
          );

          phoneHandles.forEach((handle, index) => {
            allContacts.push({
              id: `coverage-${coverage.id}-carrier-${index}`,
              name: coverage.carrier?.name || 'Unknown Carrier',
              secondaryTitle: handle.name || 'Carrier',
              phone: handle.value,
              type: ContactType.INSURANCE_CARRIER,
            });
          });
        }

        // Adjuster contacts
        if (coverage.adjusters?.[0]?.handles) {
          const phoneHandles = coverage.adjusters[0].handles.filter(
            (handle: ContactHandleSchema) => handle.handle_type === 'PHONE',
          );

          phoneHandles.forEach((handle: ContactHandleSchema, index: number) => {
            allContacts.push({
              id: `coverage-adjuster-${coverage.id}-${index}`,
              name: coverage.adjusters?.[0]?.name || 'Unknown Adjuster',
              secondaryTitle: handle.name || 'Adjuster',
              phone: handle.value,
              type: ContactType.INSURANCE_ADJUSTER,
            });
          });
        }

        // Subrogator contacts
        if (coverage.subrogator?.handles) {
          const phoneHandles = coverage.subrogator.handles.filter(
            (handle: ContactHandleSchema) =>
              handle.handle_type === 'PHONE' &&
              handle.value &&
              isPhoneNumberValid(handle.value),
          );

          phoneHandles.forEach((handle, index) => {
            allContacts.push({
              id: `coverage-${coverage.id}-subrogator-${index}`,
              name: coverage.subrogator?.name || 'Unknown Subrogator',
              secondaryTitle: handle.name || 'Subrogator',
              phone: handle.value,
              type: ContactType.INSURANCE_ADJUSTER,
            });
          });
        }
      });
    }

    // Insurance note contacts
    if (insuranceNotes) {
      allContacts.push(
        ...(insuranceNotes as InsuranceNote[])
          .filter((note) => note.adjuster_phone)
          .map((note) => ({
            id: note.id,
            name: note.carrier || 'Unknown Carrier',
            secondaryTitle: note.adjuster_name || 'Adjuster',
            phone: note.adjuster_phone || '',
            type: ContactType.INSURANCE_ADJUSTER,
          })),
      );
    }

    return allContacts;
  }, [medicalProviders, coverages, insuranceNotes]);

  return { contacts };
};

export const CallContactSelector: React.FC<CallContactSelectorProps> = ({
  form,
  matterId,
  inlineContact,
}) => {
  const { contacts } = useCallContacts(matterId);
  const phoneNumber = form.watch('phoneNumber');

  // Fetch client info from matter context
  const { data: matterContext } = useGetMatterFlattenedContextQuery(
    { matterId },
    { skip: !matterId },
  );

  const clientInfo = React.useMemo(() => {
    if (!matterContext) return null;
    const { clientPhone } = matterContext;
    const { clientName } = matterContext;
    if (!clientPhone) return null;
    // Strip extension (x, ext, etc.) for validation, but keep original for display
    const phoneWithoutExt = clientPhone.replace(/\s*(x|ext\.?)\s*\d+$/i, '');
    if (!isPhoneNumberValid(phoneWithoutExt)) return null;
    return { name: clientName || 'Client', phone: clientPhone };
  }, [matterContext]);

  const handleQuickFillClient = () => {
    if (!clientInfo) return;
    form.setValue('contact', {
      id: 'client',
      name: clientInfo.name,
      phone: clientInfo.phone,
      type: ContactType.MEDICAL_PROVIDER, // Using as a generic contact type
    });
    form.setValue('phoneNumber', clientInfo.phone);
  };

  // Categorize contacts by type
  const categorizedContacts = React.useMemo(() => {
    const medical = contacts.filter(
      (contact) => contact.type === ContactType.MEDICAL_PROVIDER,
    );
    const insurance = contacts.filter(
      (contact) =>
        contact.type === ContactType.INSURANCE_ADJUSTER ||
        contact.type === ContactType.INSURANCE_CARRIER,
    );
    const validCarrierContacts = INSURANCE_CARRIER_CONTACTS.filter(
      (carrier) => carrier.phone && isPhoneNumberValid(carrier.phone),
    );

    const suggested = inlineContact ? [inlineContact] : [];

    return { medical, insurance, validCarrierContacts, suggested };
  }, [contacts, inlineContact]);

  const getContactDisplayValue = (contact: Contact) => {
    if (contact.secondaryTitle) {
      return `${contact.name} (${contact.secondaryTitle})`;
    }
    return contact.name;
  };

  return (
    <div className="space-y-2.5">
      {/* Quick Fill - Client */}
      {clientInfo && (
        <div className="space-y-1">
          <span className="text-[10px] font-medium text-zinc-500">
            Quick fill
          </span>
          <button
            type="button"
            onClick={handleQuickFillClient}
            className="w-full flex items-center gap-2.5 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors group"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 group-hover:bg-blue-200 transition-colors">
              <UserIcon className="h-3.5 w-3.5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-[11px] font-semibold text-blue-900 truncate">
                {clientInfo.name}
              </div>
              <div className="text-[10px] text-blue-600 truncate">
                {formatPhoneNumber(clientInfo.phone)}
              </div>
            </div>
            <span className="text-[9px] font-medium text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded group-hover:bg-blue-200 transition-colors">
              Client
            </span>
          </button>
        </div>
      )}

      <FormField
        control={form.control}
        name="contact"
        render={({ field }) => (
          <FormItem className="space-y-1">
            <FormLabel className="text-[10px] font-medium text-zinc-800">
              Contact
            </FormLabel>
            <FormControl>
              <GroupedCombobox
                searchKeys={['name', 'type', 'phone', 'secondaryTitle']}
                label=""
                groups={[
                  ...(categorizedContacts.suggested.length > 0
                    ? [
                        {
                          label: 'Suggested',
                          options: categorizedContacts.suggested,
                        },
                      ]
                    : []),
                  ...(categorizedContacts.medical.length > 0
                    ? [
                        {
                          label: 'Medical Providers',
                          options: categorizedContacts.medical,
                        },
                      ]
                    : []),
                  ...(categorizedContacts.insurance.length > 0
                    ? [
                        {
                          label: 'Insurance Contacts',
                          options: categorizedContacts.insurance,
                        },
                      ]
                    : []),
                  {
                    label: 'Insurance Carriers',
                    options: categorizedContacts.validCarrierContacts,
                  },
                ]}
                value={field.value}
                onChange={(value) => {
                  if (value) {
                    field.onChange(value);
                    form.setValue('phoneNumber', value.phone);
                  }
                }}
                placeholder="Select contact"
                displayValue={getContactDisplayValue}
                renderItem={({ item: contact, isSelected }) => (
                  <div
                    className={`flex flex-col w-full min-w-0 py-1 px-0.5 rounded-sm ${
                      isSelected ? 'bg-zinc-100' : ''
                    }`}
                  >
                    <div className="font-medium text-[11px] text-zinc-900 truncate text-left">
                      {contact.name}
                    </div>
                    <div className="flex flex-col text-[9px] text-left w-full">
                      {contact.secondaryTitle && (
                        <span className="font-medium text-zinc-600 truncate">
                          {contact.secondaryTitle}
                        </span>
                      )}
                      {contact.phone && (
                        <span className="font-medium text-zinc-700 truncate">
                          {formatPhoneNumber(contact.phone)}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                renderSelection={({ item: contact }) =>
                  field.value ? (
                    <div className="grid grid-cols-[1fr_auto] gap-1.5 items-center w-full py-0.5 min-w-0">
                      <div className="flex flex-col w-full min-w-0">
                        <div className="font-medium text-[11px] text-zinc-900 truncate text-left">
                          {contact.name}
                        </div>
                        <div className="flex items-center gap-1 text-[9px] text-left w-full">
                          {contact.secondaryTitle && (
                            <span className="font-medium text-zinc-600 truncate">
                              {contact.secondaryTitle}
                            </span>
                          )}
                          {contact.phone && (
                            <span className="font-medium text-zinc-700 truncate">
                              {formatPhoneNumber(contact.phone)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        role="button"
                        tabIndex={0}
                        className="text-zinc-500 hover:text-zinc-800 cursor-pointer text-[9px] font-medium px-1 py-0.5 rounded hover:bg-zinc-100 transition-colors shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          field.onChange(null);
                          form.setValue('phoneNumber', '');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            field.onChange(null);
                            form.setValue('phoneNumber', '');
                          }
                        }}
                      >
                        Clear
                      </div>
                    </div>
                  ) : (
                    <span className="text-zinc-500 font-medium text-[10px] truncate">
                      {phoneNumber
                        ? formatPhoneNumber(phoneNumber)
                        : 'Select contact'}
                    </span>
                  )
                }
              />
            </FormControl>
            <FormMessage className="text-[9px]" />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="phoneNumber"
        render={({ field }) => (
          <FormItem className="space-y-1">
            <FormLabel className="text-[10px] font-medium text-zinc-800">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input
                type="tel"
                placeholder="e.g. (303) 217-0796"
                className="h-7 text-[11px] font-medium transition-colors data-[invalid=true]:border-red-300 data-[invalid=true]:ring-red-100"
                data-invalid={field.value && !isPhoneNumberValid(field.value)}
                {...field}
              />
            </FormControl>
            {field.value && !isPhoneNumberValid(field.value) && (
              <p className="text-[9px] font-medium text-red-600">
                Enter a valid phone number
              </p>
            )}
          </FormItem>
        )}
      />
    </div>
  );
};
